import { Middleware, combineReducers, configureStore } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import { initialState, rootReducer as legacyRootReducer } from '../reducer/reducer';
import rootSaga from '../saga/rootSaga';
import { authenticationSlice } from './slices/authenticationSlice';
import { bookingCreationNonRoomRentalSlice } from './slices/bookingCreationNonRoomRentalSlice';
import globalSlice from './slices/globalSlice';
import depotSlice from './slices/depotSlice';
import { bookingCreationSlice } from './slices/bookingCreationSlice';
import { locationsApiSlice } from '../api/locations/locationsApiSlice';
import { apartmentBookingApiSlice } from '../api/apartment/apartmentApiSlice';
import bookingPageReducer from './slices/bookingSlice';
import { bookingApiSlice } from '../api/booking/bookingApiSlice';
import { bookingArchiveApiSlice } from '../api/booking/bookingArchiveApiSlice';
import { bookingWriteApiSlice } from '../api/booking/bookingWriteApiSlice';
import { pricePreviewApiSlice } from '../api/booking/pricePreviewApiSlice';
import { seriesBookingApiSlice } from '../api/booking/seriesBookingApiSlice';
import { rentalReturnPhotosApiSlice } from '../api/booking/rentalReturnPhotosApiSlice';
import { orderUsageLogApiSlice } from '../api/booking/orderUsageLogApiSlice';
import { orderEventsApiSlice } from '../api/booking/orderEventsApiSlice';
import { paymentHistoryApiSlice } from '../api/booking/paymentHistoryApiSlice';
import { doorApiSlice } from '../api/door/doorApiSlice';
import { doorHistoryApiSlice } from '../api/door/doorHistoryApiSlice';
import { thingApiSlice } from '../api/thing/thingApiSlice';
import { issueReportApi } from '../api/issueReport/issueReportApiSlice';
import { issueReportArchiveApi } from '../api/issueReport/issueReportArchiveApiSlice';
import { groupApiSlice } from '../api/group/assetGroupApiSlice';
import { groupAdminApiSlice } from '../api/group/groupAdminApiSlice';
import { depositAdminApiSlice } from '../api/deposit/depositAdminApiSlice';
import { occupancyApiSlice } from '../api/group/occupancyApiSlice';
import { dashboardApiSlice } from '../api/dashboard/dashboardApiSlice';
import { assetApiSlice } from '../api/asset/assetApiSlice';
import { priceCategoryApiSlice } from '../api/priceCategory/priceCategoryApiSlice';
import { taxCategoryApiSlice } from '../api/taxCategory/taxCategoryApiSlice';
import { invoiceApiSlice } from '../api/invoice/invoiceApiSlice';
import { accountApiSlice } from '../api/account/accountApiSlice';
import { masterApiSlice } from '../api/master/masterApiSlice';
import { voucherApiSlice } from '../api/voucher/voucherApiSlice';
import { priceAdjustmentApiSlice } from '../api/priceAdjustment/priceAdjustmentApiSlice';
import { bannerApiSlice } from '../api/banner/bannerApiSlice';
import { accessCodeDoorApiSlice } from '../api/accessCodeDoor/accessCodeDoorApiSlice';
import { organizationApiSlice } from '../api/organization/organizationApiSlice';
import { customerAccountApiSlice } from '../api/customerAccount/customerAccountApiSlice';
import { productObjectsApiSlice } from '../api/product/productObjectsApiSlice';
import { inventoryApiSlice } from '../api/inventory/inventoryApiSlice';
import { productCategoryApiSlice } from '../api/productCategory/productCategoryApiSlice';
import { searchApiSlice } from '../api/search/searchApiSlice';
import { createTenantCacheResetMiddleware } from './tenantCacheResetMiddleware';
import { tenantArchivedMiddleware } from './tenantArchivedMiddleware';
import { registerTenantSource } from '../api/tenantCacheKey';

const sagaMiddleware = createSagaMiddleware();

// Every RTK Query slice in the app, in one place. This array is what wires the
// slices' middleware into the store, so a slice that isn't listed here simply
// does not work — which is what keeps it from drifting out of sync with the
// tenant-cache reset below (the previous hand-maintained reset list covered 6
// of 28 slices and leaked another tenant's data into the promo-code select).
const API_SLICES = [
    locationsApiSlice,
    doorApiSlice,
    doorHistoryApiSlice,
    thingApiSlice,
    apartmentBookingApiSlice,
    bookingApiSlice,
    bookingArchiveApiSlice,
    bookingWriteApiSlice,
    pricePreviewApiSlice,
    seriesBookingApiSlice,
    rentalReturnPhotosApiSlice,
    orderUsageLogApiSlice,
    orderEventsApiSlice,
    paymentHistoryApiSlice,
    issueReportApi,
    issueReportArchiveApi,
    groupApiSlice,
    groupAdminApiSlice,
    depositAdminApiSlice,
    occupancyApiSlice,
    dashboardApiSlice,
    assetApiSlice,
    priceCategoryApiSlice,
    taxCategoryApiSlice,
    invoiceApiSlice,
    accountApiSlice,
    masterApiSlice,
    voucherApiSlice,
    priceAdjustmentApiSlice,
    bannerApiSlice,
    accessCodeDoorApiSlice,
    organizationApiSlice,
    customerAccountApiSlice,
    productObjectsApiSlice,
    inventoryApiSlice,
    productCategoryApiSlice,
    searchApiSlice,
];

const rootReducer = combineReducers({
    [locationsApiSlice.reducerPath]: locationsApiSlice.reducer,
    [doorApiSlice.reducerPath]: doorApiSlice.reducer,
    [doorHistoryApiSlice.reducerPath]: doorHistoryApiSlice.reducer,
    [thingApiSlice.reducerPath]: thingApiSlice.reducer,
    [apartmentBookingApiSlice.reducerPath]: apartmentBookingApiSlice.reducer,
    [bookingApiSlice.reducerPath]: bookingApiSlice.reducer,
    [bookingArchiveApiSlice.reducerPath]: bookingArchiveApiSlice.reducer,
    [bookingWriteApiSlice.reducerPath]: bookingWriteApiSlice.reducer,
    [pricePreviewApiSlice.reducerPath]: pricePreviewApiSlice.reducer,
    [seriesBookingApiSlice.reducerPath]: seriesBookingApiSlice.reducer,
    [rentalReturnPhotosApiSlice.reducerPath]: rentalReturnPhotosApiSlice.reducer,
    [orderUsageLogApiSlice.reducerPath]: orderUsageLogApiSlice.reducer,
    [orderEventsApiSlice.reducerPath]: orderEventsApiSlice.reducer,
    [paymentHistoryApiSlice.reducerPath]: paymentHistoryApiSlice.reducer,
    [issueReportApi.reducerPath]: issueReportApi.reducer,
    [issueReportArchiveApi.reducerPath]: issueReportArchiveApi.reducer,
    [groupApiSlice.reducerPath]: groupApiSlice.reducer,
    [groupAdminApiSlice.reducerPath]: groupAdminApiSlice.reducer,
    [depositAdminApiSlice.reducerPath]: depositAdminApiSlice.reducer,
    [occupancyApiSlice.reducerPath]: occupancyApiSlice.reducer,
    [dashboardApiSlice.reducerPath]: dashboardApiSlice.reducer,
    [assetApiSlice.reducerPath]: assetApiSlice.reducer,
    [priceCategoryApiSlice.reducerPath]: priceCategoryApiSlice.reducer,
    [taxCategoryApiSlice.reducerPath]: taxCategoryApiSlice.reducer,
    [invoiceApiSlice.reducerPath]: invoiceApiSlice.reducer,
    [accountApiSlice.reducerPath]: accountApiSlice.reducer,
    [masterApiSlice.reducerPath]: masterApiSlice.reducer,
    [voucherApiSlice.reducerPath]: voucherApiSlice.reducer,
    [priceAdjustmentApiSlice.reducerPath]: priceAdjustmentApiSlice.reducer,
    [bannerApiSlice.reducerPath]: bannerApiSlice.reducer,
    [accessCodeDoorApiSlice.reducerPath]: accessCodeDoorApiSlice.reducer,
    [organizationApiSlice.reducerPath]: organizationApiSlice.reducer,
    [customerAccountApiSlice.reducerPath]: customerAccountApiSlice.reducer,
    [productObjectsApiSlice.reducerPath]: productObjectsApiSlice.reducer,
    [inventoryApiSlice.reducerPath]: inventoryApiSlice.reducer,
    [productCategoryApiSlice.reducerPath]: productCategoryApiSlice.reducer,
    [searchApiSlice.reducerPath]: searchApiSlice.reducer,
    global: globalSlice,
    legacy: legacyRootReducer,
    authentication: authenticationSlice.reducer,
    bookingCreation: bookingCreationSlice.reducer,
    bookingCreationNonRoomRental: bookingCreationNonRoomRentalSlice.reducer,
    bookingPage: bookingPageReducer,
    depotSlice: depotSlice,
});

export const store = configureStore({
    reducer: rootReducer,
    preloadedState: initialState,
    devTools: process.env.NODE_ENV !== 'production',
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            // thunk: false
            serializableCheck: false,
            immutableCheck: false,
        }).concat(
            sagaMiddleware as Middleware,
            ...API_SLICES.map((slice) => slice.middleware),
            createTenantCacheResetMiddleware(API_SLICES),
            tenantArchivedMiddleware,
        ),
});

sagaMiddleware.run(rootSaga);

// Tenant-scoped RTK Query cache keys (#1848). The serializer deliberately does
// not import the store: store → api slices → tenantCacheKey → store was a real
// cycle, and it only worked while the bundler happened to evaluate the store
// first. Registering here inverts that edge — the direction of the dependency
// now matches the direction of initialization.
//
// Read through `tenantSelector`, the same value `api/index.ts` stamps onto the
// X-Tenant header, so a cache key can never disagree with the request it keys.
registerTenantSource(() => store.getState().legacy.authentication.tenant ?? '');

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
